import React from 'react';

export const portalInputClass =
  'w-full rounded-2xl border border-[rgba(74,33,71,0.15)] bg-white px-4 py-3 font-jakarta text-[15px] text-[#2a0f2a] outline-none transition-colors placeholder:text-[#5a3158]/50 focus:border-chisme-primary/50 focus:ring-2 focus:ring-chisme-primary/15';

export const portalLabelClass =
  'mb-2 block font-jakarta text-sm font-semibold text-[#2a0f2a]';

type PortalFieldProps = {
  label: string;
  htmlFor: string;
  children: React.ReactNode;
  hint?: string;
};

export const PortalField: React.FC<PortalFieldProps> = ({ label, htmlFor, children, hint }) => (
  <div>
    <label htmlFor={htmlFor} className={portalLabelClass}>
      {label}
    </label>
    {children}
    {hint && <p className="mt-1.5 font-jakarta text-xs text-[#5a3158]">{hint}</p>}
  </div>
);

type PortalButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: 'primary' | 'secondary' | 'ghost';
  fullWidth?: boolean;
};

const buttonVariants = {
  primary: 'bg-chisme-primary text-white hover:bg-chisme-primary/90',
  secondary:
    'border border-[#2a0f2a] bg-transparent text-[#2a0f2a] hover:bg-[#2a0f2a] hover:text-white',
  ghost: 'bg-transparent text-[#5a3158] hover:bg-[rgba(74,33,71,0.06)]',
};

export function portalButtonClass({
  variant = 'primary',
  fullWidth = false,
  className = '',
}: {
  variant?: keyof typeof buttonVariants;
  fullWidth?: boolean;
  className?: string;
} = {}): string {
  return `inline-flex min-h-[44px] items-center justify-center rounded-full px-6 py-3 font-jakarta text-sm font-bold tracking-[-0.01em] transition-all duration-200 disabled:cursor-not-allowed disabled:opacity-50 ${
    fullWidth ? 'w-full' : ''
  } ${buttonVariants[variant]} ${className}`;
}

export const PortalButton: React.FC<PortalButtonProps> = ({
  variant = 'primary',
  fullWidth = false,
  className = '',
  children,
  ...props
}) => (
  <button type="button" className={portalButtonClass({ variant, fullWidth, className })} {...props}>
    {children}
  </button>
);

type PortalCheckboxProps = Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type'> & {
  label?: React.ReactNode;
};

export const PortalCheckbox: React.FC<PortalCheckboxProps> = ({
  className = '',
  label,
  id,
  ...props
}) => (
  <label
    htmlFor={id}
    className={`inline-flex shrink-0 cursor-pointer items-center gap-3 ${className}`}
  >
    <span className="relative inline-flex size-5 shrink-0 items-center justify-center">
      <input id={id} type="checkbox" className="peer sr-only" {...props} />
      <span
        aria-hidden
        className="absolute inset-0 rounded-[6px] border-2 border-chisme-primary/35 bg-white transition-colors duration-150 peer-checked:border-chisme-primary peer-checked:bg-chisme-primary peer-focus-visible:ring-2 peer-focus-visible:ring-chisme-primary/25 peer-focus-visible:ring-offset-1"
      />
      <svg
        viewBox="0 0 12 12"
        fill="none"
        className="relative z-10 size-3 text-white opacity-0 transition-opacity peer-checked:opacity-100"
        aria-hidden
      >
        <path
          d="M2.5 6l2.5 2.5 4.5-5"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </span>
    {label && <span className="min-w-0 flex-1">{label}</span>}
  </label>
);
